import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { type ReactNode, useState } from 'react';

function resolveHref(href: string): URL | null {
  try {
    return new URL(href, window.location.href);
  } catch {
    return null;
  }
}

/** The host is set apart in bold, so credentials before an `@` can't pass for it. */
function ResolvedUrl({ target }: { target: URL }) {
  const scheme = `${target.protocol}${target.host ? '//' : ''}`;
  const userinfo =
    target.username || target.password
      ? `${target.username}${target.password ? `:${target.password}` : ''}@`
      : '';
  return (
    <Typography
      data-testid="chat-link-url"
      variant="body2"
      sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
    >
      {scheme}
      {userinfo}
      <Box component="strong" data-testid="chat-link-host" sx={{ fontWeight: 700 }}>
        {target.host}
      </Box>
      {`${target.pathname}${target.search}${target.hash}`}
    </Typography>
  );
}

/** A link to another origin opens only after the reader confirms its full URL. */
export function ChatLink({ href, children }: { href?: string; children: ReactNode }) {
  const [confirming, setConfirming] = useState(false);

  const target = href ? resolveHref(href) : null;

  if (!href || !target) return <span>{children}</span>;

  if (target.origin === window.location.origin) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    );
  }

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => setConfirming(true)}
        sx={{ verticalAlign: 'baseline', font: 'inherit' }}
      >
        {children}
      </Link>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        aria-labelledby="chat-link-confirm-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="chat-link-confirm-title">Open an external link?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            This link leaves Lift AI. Check the full address before you open it:
          </DialogContentText>
          <ResolvedUrl target={target} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirming(false);
              window.open(target.href, '_blank', 'noopener,noreferrer');
            }}
          >
            Open link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
